// NOTICE: KAPTION-INTEGRATION

/**
 * Converts WhatsApp text formatting to HTML
 * WhatsApp formatting rules:
 * - *text* → <strong>text</strong> (bold)
 * - _text_ → <em>text</em> (italic)
 * - ~text~ → <del>text</del> (strikethrough)
 * - ```text``` → <code>text</code> (monospace block)
 * - `text` → <code>text</code> (inline monospace)
 */
const formatWhatsAppText = text => {
  if (!text) return '';

  let formatted = text;

  // Order matters: process multi-char markers before single-char ones

  // Triple backticks for code blocks (must come before single backticks)
  formatted = formatted.replace(
    /```([\s\S]*?)```/g,
    '<code class="wa-code-block">$1</code>'
  );

  // Single backticks for inline code
  formatted = formatted.replace(
    /`([^`]+)`/g,
    '<code class="wa-code">$1</code>'
  );

  // Bold: *text* (but not ** or * alone)
  formatted = formatted.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');

  // Italic: _text_ (but not __ or _ alone)
  formatted = formatted.replace(/_([^_\n]+)_/g, '<em>$1</em>');

  // Strikethrough: ~text~
  formatted = formatted.replace(/~([^~\n]+)~/g, '<del>$1</del>');

  // Convert newlines to <br> for proper display
  formatted = formatted.replace(/\n/g, '<br>');

  return formatted;
};

// Helper to extract value from contact object based on path
// e.g. contact.name or contact.custom_attribute.promo_code
const extractValueFromPath = (contact, path) => {
  // Strip {{ and }} if present
  const cleanPath = path.replace(/^{{|}}$/g, '').trim();

  // Handle direct text replacement (not a variable)
  if (!cleanPath.includes('.')) return cleanPath;

  const parts = cleanPath.split('.');
  // Remove 'contact' prefix if present
  if (parts[0] === 'contact') parts.shift();

  let current = contact;

  parts.forEach(part => {
    if (current === undefined || current === null) return;

    // Handle custom_attribute special case to match Chatwoot's structure
    if (part === 'custom_attribute') {
      current = current.custom_attributes;
    } else {
      current = current[part];
    }
  });

  return current !== undefined && current !== null ? current : '';
};

export function useTemplateRenderer() {
  /**
   * Renders a WhatsApp template with variable substitution and formatting
   * @param {string} templateText - The template text with {{1}}, {{2}} placeholders
   * @param {Object} variableBindings - Map of variable keys to their bound values
   * @param {Object} contact - The contact object for variable resolution
   * @param {Object} options - Rendering options
   * @param {boolean} options.format - Whether to apply WhatsApp formatting (default: false)
   * @returns {string} The rendered template text
   */
  const renderTemplate = (
    templateText,
    variableBindings,
    contact,
    options = {}
  ) => {
    if (!templateText || !contact) return '';

    let rendered = templateText;

    // Replace each variable with its bound value from contact
    Object.entries(variableBindings).forEach(([variableKey, bindingValue]) => {
      // eslint-disable-line no-restricted-syntax
      // Skip if no binding value
      if (!bindingValue) return;

      // The variableKey corresponds to the {{ 1 }}, {{ 2 }} placeholders in the template
      // We need to find these patterns and replace them, handling optional spaces
      // e.g., {{1}}, {{ 1 }}, {{  1  }} should all match
      const escapedKey = variableKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const patternRegex = new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, 'g');

      // If bindingValue looks like a liquid variable (e.g. {{contact.name}}), extract it
      // Otherwise use the literal value
      let value;
      if (bindingValue.startsWith('{{') && bindingValue.endsWith('}}')) {
        value = extractValueFromPath(contact, bindingValue);
      } else {
        value = bindingValue;
      }

      rendered = rendered.replace(patternRegex, value || '');
    });

    // Apply WhatsApp formatting if requested
    if (options.format) {
      rendered = formatWhatsAppText(rendered);
    }

    return rendered;
  };

  return { renderTemplate, formatWhatsAppText };
}
